// ============================================
// PAGAMENTO DE ATIVAÇÃO
//
// Contrato obrigatório assinado não libera o sistema sozinho: a Klinvia emite
// a cobrança na hora e o acesso só abre quando ela é confirmada. Esta tela é o
// passo seguinte à assinatura e também a tela de bloqueio de quem voltou a
// logar antes de pagar.
//
// Meios: PIX (QR e copia e cola, confirmação em segundos) e cartão de crédito
// (página de pagamento da Asaas). Boleto não entra — a compensação em dias
//  deixaria o cliente parado esperando a baixa.
// ============================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CreditCard as CardIcon,
  OpenInNew as OpenInNewIcon,
  Pix as PixIcon,
  Refresh as RefreshIcon,
  CheckCircleRounded as CheckCircleIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { isKlinviaNativeApp } from '../../utils/nativeApp';
import NativeAppBillingNotice from './NativeAppBillingNotice';

const TEAL = '#0D9488';
const NAVY = '#0F172A';

/** PIX cai em segundos; cartão pode levar alguns minutos para a Asaas confirmar. */
const POLL_MS = 8000;

export type ActivationMethod = 'PIX' | 'CREDIT_CARD';

export interface ActivationInvoice {
  asaasPaymentId: string;
  status: string;
  billingType: string | null;
  valueCents: number;
  dueDate: string;
  invoiceUrl: string | null;
  pixPayload: string | null;
  pixEncodedImage: string | null;
}

export interface ActivationState {
  required: boolean;
  paid: boolean;
  contractId: string | null;
  contractTitle: string | null;
  valueCents: number | null;
  invoice: ActivationInvoice | null;
  chargeError: string | null;
}

export function formatBRL(cents: number | null | undefined): string {
  return ((cents ?? 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(ymd: string | null): string {
  if (!ymd) return '—';
  return new Date(`${String(ymd).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR');
}

function apiError(e: unknown, fallback: string): string {
  return axios.isAxiosError(e) && e.response?.data?.error ? String(e.response.data.error) : fallback;
}

interface Props {
  /** Estado já conhecido (ex.: devolvido pela assinatura), para não piscar carregando. */
  initial?: ActivationState | null;
  /** Chamado quando o pagamento é confirmado e o acesso é liberado. */
  onReleased?: () => void;
  /** Fundo escuro na tela de bloqueio; claro dentro do modal de contrato. */
  tone?: 'light' | 'dark';
}

const PlatformActivationPayment: React.FC<Props> = ({ initial = null, onReleased, tone = 'light' }) => {
  const dark = tone === 'dark';

  const [state, setState] = useState<ActivationState | null>(initial);
  const [loading, setLoading] = useState(!initial);
  const [method, setMethod] = useState<ActivationMethod>(
    (initial?.invoice?.billingType as ActivationMethod) || 'PIX',
  );
  const [switching, setSwitching] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);
  const releasedRef = useRef(false);

  const announceRelease = useCallback(() => {
    if (releasedRef.current) return;
    releasedRef.current = true;
    onReleased?.();
  }, [onReleased]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get<{ success: boolean; data: ActivationState | null }>(
        getApiUrl('/platform-plan/activation'),
      );
      const data = res.data.data;
      setState(data);
      if (data?.invoice?.billingType === 'CREDIT_CARD') setMethod('CREDIT_CARD');
      if (!data || !data.required || data.paid) announceRelease();
    } catch (e) {
      setError(apiError(e, 'Não foi possível carregar a cobrança agora.'));
    } finally {
      setLoading(false);
    }
  }, [announceRelease]);

  useEffect(() => {
    void load();
  }, [load]);

  // Enquanto a cobrança estiver aberta, o pagamento é buscado sozinho: o
  // cliente não precisa apertar nada depois de pagar.
  useEffect(() => {
    if (!state?.required || state.paid) return undefined;
    const timer = window.setInterval(() => {
      void axios
        .post<{ success: boolean; data: { blocked: boolean; activation: ActivationState | null } }>(
          getApiUrl('/platform-plan/activation/recheck'),
        )
        .then((res) => {
          const activation = res.data.data?.activation ?? null;
          if (activation) setState(activation);
          if (!res.data.data?.blocked || activation?.paid) announceRelease();
        })
        .catch(() => undefined);
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [state?.required, state?.paid, announceRelease]);

  const chooseMethod = async (next: ActivationMethod) => {
    if (next === method || switching) return;
    setSwitching(true);
    setError(null);
    try {
      const res = await axios.post<{ success: boolean; data: ActivationState }>(
        getApiUrl('/platform-plan/activation/method'),
        { method: next },
      );
      setState(res.data.data);
      setMethod(next);
      if (res.data.data?.paid) announceRelease();
    } catch (e) {
      setError(apiError(e, 'Não foi possível trocar a forma de pagamento.'));
    } finally {
      setSwitching(false);
    }
  };

  const recheck = async () => {
    setChecking(true);
    setError(null);
    try {
      const res = await axios.post<{
        success: boolean;
        data: { blocked: boolean; activation: ActivationState | null };
      }>(getApiUrl('/platform-plan/activation/recheck'));
      const activation = res.data.data?.activation ?? null;
      if (activation) setState(activation);
      if (!res.data.data?.blocked || activation?.paid) {
        announceRelease();
      } else {
        setSnack('Pagamento ainda não consta. Assim que a Asaas confirmar, o acesso abre sozinho.');
      }
    } catch (e) {
      setError(apiError(e, 'Não foi possível verificar o pagamento agora.'));
    } finally {
      setChecking(false);
    }
  };

  const copyPix = () => {
    const payload = state?.invoice?.pixPayload;
    if (!payload) return;
    void navigator.clipboard.writeText(payload);
    setSnack('Código PIX copiado.');
  };

  const textPrimary = dark ? '#F8FAFC' : NAVY;
  const textMuted = dark ? '#94A3B8' : 'text.secondary';
  const surface = dark ? 'rgba(148,163,184,0.06)' : '#F8FAFC';
  const surfaceBorder = dark ? 'rgba(148,163,184,0.12)' : 'rgba(15,23,42,0.08)';

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
        <CircularProgress size={28} sx={{ color: TEAL }} />
      </Stack>
    );
  }

  if (state?.paid || !state?.required) {
    return (
      <Stack spacing={2} alignItems="center" sx={{ py: 5, textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 48, color: TEAL }} />
        <Typography sx={{ fontWeight: 700, fontSize: 20, color: textPrimary }}>
          Pagamento confirmado
        </Typography>
        <Typography variant="body2" sx={{ color: textMuted }}>
          Seu sistema está liberado.
        </Typography>
      </Stack>
    );
  }

  if (isKlinviaNativeApp()) return <NativeAppBillingNotice tone={tone} />;

  const invoice = state.invoice;

  return (
    <Stack spacing={2.5}>
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: surface,
          border: '1px solid',
          borderColor: surfaceBorder,
        }}
      >
        <Typography variant="overline" sx={{ color: textMuted, fontWeight: 700, letterSpacing: '0.08em' }}>
          Mensalidade da plataforma
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color: textPrimary, letterSpacing: '-0.02em', mt: 0.25 }}>
          {formatBRL(invoice?.valueCents ?? state.valueCents)}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          {state.contractTitle && (
            <Chip size="small" variant="outlined" label={state.contractTitle} sx={{ fontWeight: 600 }} />
          )}
          {invoice?.dueDate && (
            <Chip size="small" label={`Vence em ${formatDate(invoice.dueDate)}`} sx={{ fontWeight: 600 }} />
          )}
        </Stack>
      </Box>

      {state.chargeError && !invoice && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Não foi possível gerar a cobrança automaticamente: {state.chargeError} Fale com o suporte da
          Klinvia.
        </Alert>
      )}

      {invoice && (
        <>
          <ToggleButtonGroup
            value={method}
            exclusive
            fullWidth
            onChange={(_e, value) => value && void chooseMethod(value as ActivationMethod)}
            disabled={switching}
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 600,
                py: 1.1,
                borderRadius: 2,
                color: dark ? '#CBD5E1' : undefined,
                borderColor: dark ? 'rgba(148,163,184,0.3)' : undefined,
              },
              '& .Mui-selected': { bgcolor: `${TEAL} !important`, color: '#fff !important' },
            }}
          >
            <ToggleButton value="PIX">
              <PixIcon fontSize="small" sx={{ mr: 0.75 }} />
              PIX
            </ToggleButton>
            <ToggleButton value="CREDIT_CARD">
              <CardIcon fontSize="small" sx={{ mr: 0.75 }} />
              Cartão de crédito
            </ToggleButton>
          </ToggleButtonGroup>

          {switching && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: textMuted }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Atualizando a cobrança…</Typography>
            </Stack>
          )}

          {!switching && method === 'PIX' && (
            <Stack spacing={1.5} alignItems="center">
              {invoice.pixEncodedImage ? (
                <Box
                  component="img"
                  src={`data:image/png;base64,${invoice.pixEncodedImage}`}
                  alt="QR Code do PIX de ativação"
                  sx={{ width: 208, height: 208, borderRadius: 2, bgcolor: '#fff', p: 1 }}
                />
              ) : (
                <Alert severity="info" sx={{ borderRadius: 2, width: '100%' }}>
                  O QR ainda está sendo gerado pela Asaas. Use o link de pagamento abaixo ou tente em
                  instantes.
                </Alert>
              )}
              {invoice.pixPayload && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CopyIcon />}
                  onClick={copyPix}
                  sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#0F766E' }, fontWeight: 700, borderRadius: 2, py: 1.1 }}
                >
                  Copiar código PIX (copia e cola)
                </Button>
              )}
              <Typography variant="caption" sx={{ color: textMuted, textAlign: 'center' }}>
                Assim que o PIX cair, o sistema libera sozinho — normalmente em poucos segundos.
              </Typography>
            </Stack>
          )}

          {!switching && method === 'CREDIT_CARD' && (
            <Stack spacing={1.5}>
              {invoice.invoiceUrl && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<OpenInNewIcon />}
                  href={invoice.invoiceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#0F766E' }, fontWeight: 700, borderRadius: 2, py: 1.25 }}
                >
                  Pagar com cartão na Asaas
                </Button>
              )}
              <Typography variant="caption" sx={{ color: textMuted }}>
                A página de pagamento abre em outra aba. Volte aqui depois de concluir: a confirmação
                chega automaticamente.
              </Typography>
            </Stack>
          )}
        </>
      )}

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        startIcon={checking ? <CircularProgress size={16} /> : <RefreshIcon />}
        onClick={() => void recheck()}
        disabled={checking}
        sx={{ alignSelf: 'flex-start', fontWeight: 600, color: dark ? '#93C5FD' : TEAL, textTransform: 'none' }}
      >
        {checking ? 'Verificando…' : 'Já paguei, verificar agora'}
      </Button>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Stack>
  );
};

export default PlatformActivationPayment;
